import React, {
    useMemo,
    useState,
    useEffect,
} from "react";
import "./DetailsToolPanel.styles.scss";

// Package Imports
import { CustomToolPanelProps } from "ag-grid-react";

// Type Imports
import { IAGAlgoDataRow } from "@/pages/AlgoManagement/components/AlgoTable/AlgoTable.types";
import { humanizeKey, formatArrayValue } from "@/pages/AlgoManagement/utils";

// MUI Imports
import { Button, useTheme } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

type DetailsContext = {
    openFullDetails?: (row: IAGAlgoDataRow) => void;
};

function formatValue(value: unknown): string {
    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return formatArrayValue(value);
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

export interface DetailsToolPanelProps extends CustomToolPanelProps {
    registerSetRow?: (fn: (row: IAGAlgoDataRow | null) => void) => void;
}

const DetailsToolPanel: React.FC<DetailsToolPanelProps> = ({ context, registerSetRow }) => {
    const { openFullDetails } = (context || {}) as DetailsContext;
    const theme = useTheme();

    const [selectedRow, setSelectedRow] = useState<IAGAlgoDataRow | null>(null);

    // Register the setRow function when component mounts
    useEffect(() => {
        if (registerSetRow) {
            registerSetRow(setSelectedRow);
        }
    }, [registerSetRow]);

    const rows = useMemo(() => {
        if (!selectedRow) return [];

        const instance = selectedRow.original_data || {};
        const params = selectedRow.visible_parameters || [];

        return params.map((key) => {
            const parameters = (instance as any).parameters || {};
            const value =
                (parameters && key in parameters ? parameters[key] : undefined) ??
                ((instance as any)[key] as unknown);
            return { key, value: formatValue(value) };
        });
    }, [selectedRow]);

    if (!selectedRow) {
        return (
            <div className="details-tool-panel">
                <div className="details-tool-panel__empty">
                    Double-click a row to see details.
                </div>
            </div>
        );
    }

    return (
        <div className="details-tool-panel">
            <div className="details-tool-panel__header">
                <div className="details-tool-panel__title">Details</div>
                {openFullDetails && (
                    <Button
                        variant="text"
                        startIcon={<OpenInNewIcon />}
                        sx={{
                            color: (theme.palette.secondary as any)["300"] || theme.palette.primary.main,
                            "&:hover": {
                                color: (theme.palette.secondary as any)["300"] || theme.palette.primary.dark,
                            },
                        }}
                        onClick={() => openFullDetails(selectedRow)}
                    >
                        View All
                    </Button>
                )}
            </div>
            <div className="details-tool-panel__list">
                {rows.length === 0 ? (
                    <div className="details-tool-panel__empty">
                        No visible parameters for this bot.
                    </div>
                ) : (
                    rows.map((row: { key: string; value: string }) => (
                        <div key={row.key} className="details-tool-panel__row">
                            <div className="details-tool-panel__label">
                                {humanizeKey(row.key)}
                            </div>
                            <div className="details-tool-panel__value">{row.value}</div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default DetailsToolPanel;